/*
 * HLTH free shaker on the first subscription
 *
 * Only at checkout: on the checkout press, if the cart holds enough subscribed qualifying
 * items (each qualifier is a product and a minimum subscribed quantity), add one shaker
 * (hidden property _free_gift) and the shaker code, keep every code already on the cart,
 * then open checkout. Whenever the shopper is back in the store (page load, Back from
 * checkout, any cart change) the gift line and the code come out again, so the cart
 * itself never holds the shaker; the drawer shows it as a display row instead.
 *
 * The code (Buy X get Y, buys side purchase type Subscription, once per customer) is what
 * makes the shaker free; this script only puts the line and the code in the cart so
 * checkout and the confirmation show them.
 *
 * Never charged: after applying, the cart is re-read; a gift that isn't fully free (code
 * refused, e.g. a logged-in customer who has used it) is taken back out with the code. A
 * shaker the shopper bought from the upsell is never removed; if one is in the cart, no
 * gift is added and the code frees that one.
 *
 * Subscription minimum backstop: if window.HLTH_SUB_MIN is set and the cart holds fewer
 * subscribed bags than the minimum, the press doesn't open checkout; the drawer is
 * re-rendered so its notice shows.
 *
 * Why go straight to /checkout instead of letting the drawer form submit: the form posts
 * updates[] by line position, and the line we add shifts the positions. The cart note is
 * saved here instead.
 *
 * Never blocks a sale on our account: any error, or no answer within TIMEOUT_MS, opens
 * checkout anyway.
 */
(function () {
  const cfg = window.HLTH_FREE_SHAKER;
  if (!cfg || !cfg.enabled || !cfg.shakerVariantId || !cfg.code) return;
  const QUALIFIERS = (cfg.qualifiers || [])
    .map((q) => ({ productId: Number(q.productId), minQty: Number(q.minQty) || 1 }))
    .filter((q) => q.productId);
  if (!QUALIFIERS.length) return;

  const SOURCE = 'hlth-free-shaker';
  const SHAKER_VARIANT_ID = Number(cfg.shakerVariantId);
  const CODE = String(cfg.code);
  const GIFT_PROP = '_free_gift';
  const TIMEOUT_MS = 4000;
  const BLOCKED = 'blocked';
  const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"], a[href="/checkout"]';
  const DRAWER_SECTIONS = 'cart-drawer,cart-icon-bubble';
  // Set when the press sends the shopper to checkout. Back can show the browser's cached copy
  // of the page from before the press, whose giftInCart flag is stale; this note is not.
  const PENDING_KEY = 'hlth_free_shaker_pending';

  let busy = false;

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  const isShaker = (item) => Number(item.variant_id) === SHAKER_VARIANT_ID;
  const isGift = (item) => isShaker(item) && item.properties && item.properties[GIFT_PROP];
  const isCode = (code) => String(code).toUpperCase() === CODE.toUpperCase();
  const cartCodes = (cart) => (cart.discount_codes || []).map((d) => d.code);

  function subscribedQty(cart, productId) {
    return cart.items
      .filter((item) => Number(item.product_id) === productId && item.selling_plan_allocation)
      .reduce((n, item) => n + item.quantity, 0);
  }

  function qualifies(cart) {
    if (cfg.customerClaimed) return false;
    return QUALIFIERS.some((q) => subscribedQty(cart, q.productId) >= q.minQty);
  }

  function shortOfMinimum(cart) {
    const min = window.HLTH_SUB_MIN;
    if (!min || !min.productId) return false;
    const n = subscribedQty(cart, Number(min.productId));
    return n > 0 && n < (Number(min.minQty) || 2);
  }

  function shakerIsFree(cart) {
    const line = cart.items.find(isShaker);
    if (!line) return false;
    const off = line.line_level_discount_allocations
      .filter((a) => isCode(a.discount_application.title))
      .reduce((sum, a) => sum + a.amount, 0);
    return off >= line.original_price;
  }

  // Redraw the drawer and the cart count from a sections response without opening the drawer.
  function renderQuietly(sections) {
    if (!sections) return;
    const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    const drawer = document.querySelector('cart-drawer');
    const inner = document.getElementById('CartDrawer');
    if (sections['cart-drawer'] && inner) {
      const fresh = parse(sections['cart-drawer'], '#CartDrawer');
      if (fresh) inner.innerHTML = fresh.innerHTML;
      const overlay = document.getElementById('CartDrawer-Overlay');
      if (overlay && drawer && typeof drawer.close === 'function') overlay.addEventListener('click', () => drawer.close());
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (sections['cart-icon-bubble'] && bubble) {
      const fresh = parse(sections['cart-icon-bubble'], '.shopify-section');
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }
  }

  async function refreshDrawer() {
    const res = await fetch('/?sections=' + DRAWER_SECTIONS);
    if (res.ok) renderQuietly(await res.json());
  }

  // Take the gift line and the code out. Returns the sections from /cart/change.js when a line went.
  async function removeGift(cart, render) {
    let sections = null;
    const gift = cart.items.find(isGift);
    if (gift) {
      const body = { id: gift.key, quantity: 0 };
      if (render) {
        body.sections = DRAWER_SECTIONS;
        body.sections_url = window.location.pathname;
      }
      sections = (await post('/cart/change.js', body)).sections || null;
    }
    const codes = cartCodes(cart);
    if (codes.some(isCode)) await post('/cart/update.js', { discount: codes.filter((c) => !isCode(c)).join(',') });
    return sections;
  }

  async function prepare() {
    const cart = await getCart();
    if (shortOfMinimum(cart)) return BLOCKED;
    if (!qualifies(cart)) {
      if (cart.items.some(isGift) || cartCodes(cart).some(isCode)) await removeGift(cart, false);
      return;
    }
    if (!cart.items.some(isShaker)) {
      await post('/cart/add.js', { items: [{ id: SHAKER_VARIANT_ID, quantity: 1, properties: { [GIFT_PROP]: '1' } }] });
    }
    const codes = cartCodes(cart);
    if (!codes.some(isCode)) await post('/cart/update.js', { discount: codes.concat(CODE).join(',') });
    let after = await getCart();
    if (!cartCodes(after).some(isCode)) {
      // A code write racing ours (the drawer's Boost call just after an add) can land on top; write once more.
      await post('/cart/update.js', { discount: cartCodes(after).concat(CODE).join(',') });
      after = await getCart();
    }
    if (!shakerIsFree(after)) await removeGift(after, false);
  }

  function saveNote(form) {
    const note = form && form.querySelector('[name="note"]');
    return note ? post('/cart/update.js', { note: note.value }).catch(() => {}) : Promise.resolve();
  }

  function markPending() {
    try { sessionStorage.setItem(PENDING_KEY, '1'); } catch (e) { /* noop */ }
  }

  function takePending() {
    try {
      const pending = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
      return !!pending;
    } catch (e) {
      return false;
    }
  }

  function release(button) {
    busy = false;
    if (button) button.removeAttribute('aria-busy');
  }

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest && event.target.closest(CHECKOUT_SELECTOR);
      if (!button || button.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      busy = true;
      button.setAttribute('aria-busy', 'true');
      const form = button.form || document.getElementById(button.getAttribute('form') || '');
      const timeout = new Promise((resolve) => setTimeout(resolve, TIMEOUT_MS));
      let outcome;
      try {
        outcome = await Promise.race([Promise.all([saveNote(form), prepare()]).then((r) => r[1]), timeout]);
      } catch (err) {
        console.error('[hlth-free-shaker]', err);
      }
      if (outcome === BLOCKED) {
        release(button);
        refreshDrawer().catch(() => {});
        return;
      }
      markPending();
      window.location.href = '/checkout';
    },
    true
  );

  // ---- Back in the store: take the gift and the code out ----
  let takingBack = false;
  async function takeBack(event) {
    if ((event && event.source === SOURCE) || takingBack || busy) return;
    takingBack = true;
    try {
      const cart = await getCart();
      if (!(cart.items.some(isGift) || cartCodes(cart).some(isCode))) return;
      const sections = await removeGift(cart, true);
      renderQuietly(sections);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
      }
    } catch (err) {
      console.error('[hlth-free-shaker] take back', err);
    } finally {
      takingBack = false;
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, takeBack);
  }

  // Back button from checkout restores this page from the browser cache, mid-press.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    release(document.querySelector('[aria-busy="true"]'));
    takePending();
    takeBack();
  });

  if (takePending() || cfg.giftInCart) takeBack();
})();
